import { useEffect, useRef, type ReactNode } from 'react'
import { IconButton } from './IconButton'
import { SectionLabel } from './SectionLabel'
import styles from './Modal.module.css'

export interface ModalProps {
  /** The dialog's accessible name, and the heading it shows. */
  title: string
  children: ReactNode
  onClose: () => void
}

/**
 * A dialog over the page, for content that is neither a picture nor part of the screen's flow.
 *
 * **The app deliberately has almost none of these.** Every editing surface is an inline form,
 * because a modal on a 390px phone is a screen that hides the thing it is about. What is left
 * for a dialog is content with no place in the flow at all — reference material you open,
 * read and dismiss without changing anything. The composer's help is exactly that.
 *
 * It is a sibling of {@link Lightbox} rather than a generalization of it: a lightbox is a
 * picture with the chrome taken away, and this is chrome with a picture nowhere in it.
 *
 * Escape closes, and so does a click on the scrim. Focus moves to the close button on open and
 * is trapped inside while it is open, because a dialog whose Tab key walks out into the page
 * behind it is a dialog a keyboard user cannot get out of.
 */
export function Modal({ title, children, onClose }: ModalProps) {
  const frame = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
        return
      }

      if (event.key !== 'Tab' || frame.current === null) return

      // The focus trap, in the smallest form that works: find what is focusable now — the
      // list changes as content expands — and wrap at either end.
      const focusable = frame.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (first === undefined || last === undefined) return

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  // The page behind must not scroll under the dialog — on a phone a drag anywhere would
  // otherwise move it, and closing would land the reader somewhere else.
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  return (
    <div className={styles.scrim} onClick={onClose}>
      <div
        ref={frame}
        className={styles.frame}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <header className={styles.head}>
          <SectionLabel tone="ember">{title}</SectionLabel>
          <IconButton label="Close" icon="close" autoFocus onClick={onClose} />
        </header>

        <div className={styles.body}>{children}</div>
      </div>
    </div>
  )
}
